import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { User } from '../shared/models/user';
import { SerializationHelper } from '../shared/helpers';
import { StoreHelper } from '../shared/helpers/store.helper';
import { HeaderService } from '../shared/services/header.service';
import { environment } from '../../environments/environment';
import { HttpService } from '../shared/services/http.service';

@Injectable()
export class AuthService {
  private baseUrl = '';
  private headers: HttpHeaders;
  token: string = '';

  constructor(private router: Router, private http: HttpClient, private headerService: HeaderService, private httpService: HttpService) {
    this.token = null;
    this.baseUrl = this.headerService.getBaseUrl();
  }

  isAuthenticated() {
    return StoreHelper.getCurrentUser() != null;
  }

  isAuthenticatedHr() {
    const user = StoreHelper.getCurrentUser();
    return (user && user.hr);
  }

  isSuper(){
    const user : any = StoreHelper.getCurrentUser()
    return user && (user.super == true || user.super == 'true')
  }

  isEducation(){
    const user: any = StoreHelper.getCurrentUser()
    return user && user.kind == 'EDUCATION'
  }

  navigateToWelcome() {
    if (this.isAuthenticated()) {
      this.router.navigate(['/start']);
    }
  }

  storeUser(user: User) {
    user.session_guid = User.makeid();
    this.token = user.token;
    this.headerService.setToken(this.token);
    const userForStorage = { ...user };
    delete userForStorage.token;
    delete userForStorage.refreshToken;
    StoreHelper.setCurrentUser(userForStorage as User);
    return user;
  }

  signupUser(tmpUser: User) {
    tmpUser.product = environment.instanceType;
    return this.http.post(
      this.baseUrl + 'user/register',
      tmpUser
    ).pipe(
      map((response: any) => {
        if (!response.success) {
          throw { statusText: response.message };
        }
        let user = SerializationHelper.toInstance(new User(), JSON.stringify(response));
        if (user && user.token) {
          user = this.storeUser(user);
        }
        return user;
      }),
      catchError((error: any) => {
        return throwError(this.headerService.getError(error));
      })
    );
  }

  signinUser(email: string, password: string) {
    return this.http.post(
      this.baseUrl + 'user/login',
      { email: email, password: password, product: environment.instanceType }
    ).pipe(
      map((response: any) => {
        let user = SerializationHelper.toInstance(new User(), JSON.stringify(response));
        if (user && user.token) {
          user = this.storeUser(user);
        }
        return user;
      }),
      catchError((error: any) => {
        return throwError(this.headerService.getError(error));
      })
    );
  }

  async signin( email, password ){
    const activationCode = StoreHelper.getObject('activationCode')
    const resp :any = await this.httpService.postAsync('user/login', {email, password, activationCode})
    if( resp.success ){
      // Store token in memory only
      this.token = resp.token

      // Store non-sensitive user info in localStorage (strip tokens)
      const userForStorage = { ...resp };
      delete userForStorage.token;
      localStorage.setItem('currentUser', JSON.stringify(userForStorage));
      localStorage.setItem('lastActivityTime', '' + new Date().getTime());

      this.headerService.setToken(this.token)
    }
    return resp;
  }

  async ssoSignin( userObject ){
    // Store token in memory only
    this.token = userObject.token

    // Store non-sensitive user info in localStorage (strip tokens)
    const userForStorage = { ...userObject };
    delete userForStorage.token;
    localStorage.setItem('currentUser', JSON.stringify(userForStorage));
    localStorage.setItem('lastActivityTime', '' + new Date().getTime());

    this.headerService.setToken(this.token)
    return userObject
  }

  async xapiSignin( activationCode, email, userId, courseId, courseCode, username, hash  ){
    const resp :any = await this.httpService.postAsync('user/xapi', {email, username, userId, courseId, courseCode, activationCode, hash})
    if( resp.success ){
      // Store token in memory only
      this.token = resp.token

      // Store non-sensitive user info in localStorage (strip tokens)
      const userForStorage = { ...resp };
      delete userForStorage.token;
      delete userForStorage.refreshToken;
      localStorage.setItem('currentUser', JSON.stringify(userForStorage));
      localStorage.setItem('lastActivityTime', '' + new Date().getTime());

      this.headerService.setToken(this.token)
    }
    return resp;
  }

  async existingEmail( email ){

    const resp :any = await this.httpService.postAsync('user/checkEmail', {email})
    if( resp.existing ){
      return true
    }
    return false
  }

  isExistingUser(email: string) {
    return this.http.post(
      this.baseUrl + 'email_check',
      { email }
    ).pipe(
      map((response: any) => {
        return true;
      }),
      catchError((error: any) => {
        return throwError(error);
      })
    );
  }


  forgotUser(email: string) {
    return this.http.post(
      this.baseUrl + 'user/sendResetPasswordEmail',
      { email, web_url: window.location.origin }
    );
  }

  resetUser(token: string, pwd: string) {
    return this.http.post(
      this.baseUrl + 'user/resetPassword',
      { token, pwd }
    ).pipe(
      catchError((error: any) => {
        return throwError(this.headerService.getError(error));
      })
    );
  }

  validateTokenAndGetContent(token: string) {
    return this.http.post( this.baseUrl + 'magictoken',
    { token }).pipe(
      catchError((error: any) => {
        return throwError(this.headerService.getError(error));
      })
    );
  }

  logout() {
    const headers = this.headerService.getHeaders();
    return this.http.delete(
        this.baseUrl + 'user/logout',
        { headers, withCredentials: true }
    ).pipe(
      map(() => {
        this.token = null;
        this.headerService.setToken(null);
        StoreHelper.clearUser();
      }),
      catchError((error: any) => {
        // Clear local state even if server call fails
        this.token = null;
        this.headerService.setToken(null);
        StoreHelper.clearUser();
        return throwError(this.headerService.getError(error));
      })
    );
  }

  getUser() {
    let user = null;
    if (this.isAuthenticated()) {
      user = StoreHelper.getCurrentUser();
    }
    return user;
  }

  /**
   * Called on app init to re-establish the session from the HttpOnly refresh token cookie.
   * If the cookie is valid, the backend returns a fresh access token.
   */
  async bootstrapToken(): Promise<boolean> {
    if (!StoreHelper.getCurrentUser()) {
      return false;
    }

    try {
      const resp: any = await this.httpService.postAsync('user/refreshToken', {});
      if (resp && resp.token) {
        this.storeUser(resp);
        return true;
      }
    } catch (e) {
      console.warn('Token bootstrap failed — no valid session', e);
    }

    // Force logout to clear stale localStorage session if refresh fails
    this.token = null;
    this.headerService.setToken(null);
    StoreHelper.clearUser();
    this.router.navigate(['/signin']);

    return false;
  }

  /**
   * Returns a valid (non-expired) access token, refreshing it via the
   * HttpOnly refresh-token cookie first if the current token is missing,
   * expired, or about to expire.
   *
   * Use this when the token is embedded directly in a URL (e.g. report /
   * download links opened via window.open). Those requests bypass the
   * AuthInterceptor, so an expired token would otherwise be rejected by
   * the backend with no chance to refresh and retry.
   */
  async getValidToken(): Promise<string> {
    if (!this.isTokenExpired(this.token)) {
      return this.token;
    }

    try {
      const resp: any = await this.httpService.postAsync('user/refreshToken', {});
      if (resp && resp.token) {
        this.token = resp.token;
        this.headerService.setToken(this.token);
      }
    } catch (e) {
      console.warn('Failed to refresh token for download URL', e);
    }

    return this.token;
  }

  /**
   * Decodes the JWT and returns true if it is missing, malformed, or
   * expires within the safety buffer. Undecodable tokens are treated as
   * expired so the caller refreshes rather than sending a dead token.
   */
  private isTokenExpired(token: string, bufferSeconds = 30): boolean {
    if (!token) {
      return true;
    }
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      if (!payload || !payload.exp) {
        return true;
      }
      const nowSeconds = Date.now() / 1000;
      return payload.exp - bufferSeconds <= nowSeconds;
    } catch (e) {
      return true;
    }
  }
}
